/**
 * Browser compatibility check utility
 */

export function checkBrowserCompatibility(): { supported: boolean; message?: string } {
  // Check for ES2015+ features
  try {
    // Check for basic ES6 features using feature detection
    // Arrow functions - check if Function.prototype.bind exists (ES5)
    if (typeof Function.prototype.bind === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support modern JavaScript features. Please use a modern browser.' 
      };
    }
    
    // Check for const/let support by checking for block scoping
    // This is implicit in modern browsers that support other ES6 features
    
    // Check for template literals by checking for String.raw
    if (typeof String.raw === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support template literals. Please use a modern browser.' 
      };
    }
    
    // Check for Promise support
    if (typeof Promise === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support Promises. Please use a modern browser.' 
      };
    }
    
    // Check for fetch API
    if (typeof fetch === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support the Fetch API. Please use a modern browser.' 
      };
    }
    
    // Check for Map/Set
    if (typeof Map === 'undefined' || typeof Set === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support Map/Set. Please use a modern browser.' 
      };
    }
    
    // Check for Symbol
    if (typeof Symbol === 'undefined') {
      return { 
        supported: false, 
        message: 'Your browser does not support Symbol. Please use a modern browser.' 
      };
    }
    
    // Check for Object methods
    if (!Object.assign || !Object.entries || !Object.values) {
      return { 
        supported: false, 
        message: 'Your browser does not support modern Object methods. Please use a modern browser.' 
      };
    }
    
    // Check for Array methods
    if (!Array.from || !Array.prototype.includes || !Array.prototype.find) {
      return { 
        supported: false, 
        message: 'Your browser does not support modern Array methods. Please use a modern browser.' 
      };
    }
    
    return { supported: true };
  } catch {
    return { 
      supported: false, 
      message: 'Your browser does not support modern JavaScript features. Please upgrade to a newer browser.' 
    };
  }
}

export function getBrowserInfo(): { name: string; version: string } {
  const userAgent = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';
  
  if (userAgent.includes('Chrome')) {
    name = 'Chrome';
    version = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Safari')) {
    name = 'Safari';
    version = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    name = 'Firefox';
    version = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('Edge')) {
    name = 'Edge';
    version = userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    name = 'Internet Explorer';
    version = userAgent.match(/(?:MSIE |rv:)(\d+)/)?.[1] || 'Unknown';
  }
  
  return { name, version };
}